import type { CustomerData, SelectionMap, Site } from './addUserTypes';

function siteHasAllPlantsSelected(site: Site, selectedPlants: SelectionMap): boolean {
    if (site.plants.length === 0) {
        return false;
    }
    for (const plant of site.plants) {
        if (!selectedPlants[plant.plantId]) {
            return false;
        }
    }
    return true;
}

function customerHasAllSitesSelected(customer: CustomerData, selectedPlants: SelectionMap): boolean {
    if (customer.sitesAndPlants.length === 0) {
        return false;
    }
    for (const site of customer.sitesAndPlants) {
        if (!siteHasAllPlantsSelected(site, selectedPlants)) {
            return false;
        }
    }
    return true;
}

export function updateParentSelections(
    customersAndSites: CustomerData[],
    selectedPlants: SelectionMap
): SelectionMap {
    const newSelected = { ...selectedPlants };

    for (const customer of customersAndSites) {
        for (const site of customer.sitesAndPlants) {
            if (siteHasAllPlantsSelected(site, selectedPlants)) {
                newSelected[site.siteId] = true;
            }
        }
        if (customerHasAllSitesSelected(customer, selectedPlants)) {
            newSelected[customer.customerId] = true;
        }
    }

    return newSelected;
}

export function buildSelectAllState(
    customersAndSites: CustomerData[],
    selectedItems: SelectionMap,
    checked: boolean
): SelectionMap {
    const newSelected = { ...selectedItems };
    customersAndSites.forEach(customer => {
        newSelected[customer.customerId] = checked;
        customer.sitesAndPlants.forEach(site => {
            newSelected[site.siteId] = checked;
            site.plants.forEach(plant => {
                newSelected[plant.plantId] = checked;
            });
        });
    });
    return newSelected;
}

export function buildCustomerSelectState(
    customersAndSites: CustomerData[],
    selectedItems: SelectionMap,
    customerId: string,
    checked: boolean
): SelectionMap {
    const newSelected = { ...selectedItems };
    newSelected[customerId] = checked;

    const customer = customersAndSites.find(c => c.customerId === customerId);
    if (!customer) {
        return newSelected;
    }

    customer.sitesAndPlants.forEach(site => {
        newSelected[site.siteId] = checked;
        site.plants.forEach(plant => {
            newSelected[plant.plantId] = checked;
        });
    });
    return newSelected;
}

export function buildSiteSelectState(
    customersAndSites: CustomerData[],
    selectedItems: SelectionMap,
    siteId: string,
    customerId: string,
    checked: boolean
): SelectionMap {
    const newSelected = { ...selectedItems };
    newSelected[siteId] = checked;

    const customer = customersAndSites.find(c => c.customerId === customerId);
    const site = customer?.sitesAndPlants.find(s => s.siteId === siteId);
    if (!site) {
        return newSelected;
    }

    site.plants.forEach(plant => {
        newSelected[plant.plantId] = checked;
    });

    if (customer) {
        const allSitesSelected = customer.sitesAndPlants.every(s =>
            s.siteId === siteId ? checked : newSelected[s.siteId]
        );
        newSelected[customerId] = allSitesSelected;
    }

    return newSelected;
}

export function buildPlantSelectState(
    customersAndSites: CustomerData[],
    selectedItems: SelectionMap,
    plantId: string,
    siteId: string,
    customerId: string,
    checked: boolean
): SelectionMap {
    const newSelected = { ...selectedItems };
    newSelected[plantId] = checked;

    const customer = customersAndSites.find(c => c.customerId === customerId);
    const site = customer?.sitesAndPlants.find(s => s.siteId === siteId);
    if (!site) {
        return newSelected;
    }

    const allPlantsSelected = site.plants.every(p =>
        p.plantId === plantId ? checked : newSelected[p.plantId]
    );
    newSelected[siteId] = allPlantsSelected;

    if (customer) {
        const allSitesSelected = customer.sitesAndPlants.every(s => {
            if (s.siteId === siteId) {
                return allPlantsSelected;
            }
            return newSelected[s.siteId];
        });
        newSelected[customerId] = allSitesSelected;
    }

    return newSelected;
}

export function customerHasParentSelection(
    customersAndSites: CustomerData[],
    selectedItems: SelectionMap
): boolean {
    return customersAndSites.some(customer =>
        selectedItems[customer.customerId] ||
        customer.sitesAndPlants.some(site => selectedItems[site.siteId])
    );
}

export function getTotalPlantCount(customersAndSites: CustomerData[]): number {
    return customersAndSites.reduce((total, customer) =>
        total + customer.sitesAndPlants.reduce((siteTotal, site) =>
            siteTotal + site.plants.length, 0), 0);
}
